// PFMEA SmartSuggest Service
// Routes all AI requests through the secure Magnus-Core backend
// NO API KEYS IN FRONTEND - All AI calls go through backend with RBAC
// ============================================================

import { authenticatedFetchJSON } from '../utils/authenticatedFetch';

// Types for AI suggestions
export interface FailureModeSuggestion {
    failureMode: string;
    failureEffect: string;
    failureCause: string;
    processElement: 'Man' | 'Machine' | 'Material' | 'Method';
    severity: number;
    occurrence: number;
    detection: number;
    actionPriority: 'H' | 'M' | 'L';
    preventionControls: string[];
    detectionControls: string[];
    rationale: string;
}

export interface SmartSuggestRequest {
    processStepName: string;
    processStepNumber?: string;
    processFunction?: string;
    industry?: string;
    existingFailureModes?: string[];
}

export interface SmartSuggestResponse {
    suggestions: FailureModeSuggestion[];
    analysisNotes: string;
    recommendedActions: string[];
}

// Calculate Action Priority per AIAG-VDA 2019
export const calculateActionPriority = (s: number, o: number, d: number): 'H' | 'M' | 'L' => {
    if (s >= 9 || (s >= 8 && o >= 4)) return 'H';
    if (s >= 7 && o >= 4 && d >= 4) return 'H';
    if (s >= 7 || (s >= 5 && o >= 4)) return 'M';
    return 'L';
};

// 4M Cause Category Mapping
const PROCESS_ELEMENT_MAP: Record<string, 'Man' | 'Machine' | 'Material' | 'Method'> = {
    'operator': 'Man',
    'training': 'Man',
    'skill': 'Man',
    'fatigue': 'Man',
    'equipment': 'Machine',
    'calibration': 'Machine',
    'tool': 'Machine',
    'wear': 'Machine',
    'raw material': 'Material',
    'contamination': 'Material',
    'incoming': 'Material',
    'specification': 'Material',
    'procedure': 'Method',
    'instruction': 'Method',
    'sequence': 'Method',
    'parameter': 'Method',
};

// Determine 4M category from cause text
export const infer4MCategory = (causeText: string): 'Man' | 'Machine' | 'Material' | 'Method' => {
    const lower = causeText.toLowerCase();
    for (const [keyword, category] of Object.entries(PROCESS_ELEMENT_MAP)) {
        if (lower.includes(keyword)) return category;
    }
    return 'Method'; // Default
};

// Helper to clean Markdown code blocks from JSON string
const cleanJson = (text: string): string => {
    if (!text) return text;
    // Remove ```json and ``` patterns
    return text.replace(/```json\n?|```/g, '').trim();
};

// SmartSuggest Prompt (sent to backend for processing)
const buildSmartSuggestPrompt = (request: SmartSuggestRequest): string => {
    return `You are an expert PFMEA (Process Failure Mode and Effects Analysis) engineer with deep knowledge of:
- AIAG-VDA 2019 PFMEA methodology
- 4M analysis (Man, Machine, Material, Method)
- Process control and detection methods
- Manufacturing best practices across industries

## TASK
Analyze the following process step and generate potential failure modes with complete PFMEA data.

## PROCESS STEP INFORMATION
- **Step Name**: ${request.processStepName}
${request.processStepNumber ? `- **Step Number**: ${request.processStepNumber}` : ''}
${request.processFunction ? `- **Function/Purpose**: ${request.processFunction}` : ''}
${request.industry ? `- **Industry**: ${request.industry}` : ''}
${request.existingFailureModes?.length ? `- **Already Identified Failure Modes** (Do NOT repeat): ${request.existingFailureModes.join(', ')}` : ''}

## REQUIREMENTS
For each failure mode, provide:
1. **failureMode**: Specific description of HOW the process can fail
2. **failureEffect**: Impact on next operation, end user, and safety
3. **failureCause**: Root cause in 4M terms
4. **processElement**: One of "Man", "Machine", "Material", "Method"
5. **severity**: 1-10 rating based on AIAG-VDA
6. **occurrence**: 1-10 rating based on process capability
7. **detection**: 1-10 rating based on detection method
8. **preventionControls**: Array of controls to PREVENT the cause
9. **detectionControls**: Array of controls to DETECT before customer
10. **rationale**: Brief explanation of why this failure mode is relevant

## OUTPUT FORMAT
Return a JSON object with:
{
    "suggestions": [array of 3-5 failure mode objects],
    "analysisNotes": "Brief summary of key risk areas",
    "recommendedActions": ["Array of suggested improvement actions"]
}

Generate 3-5 distinct failure modes covering different 4M categories.`;
};

// Main SmartSuggest Function - Routes through backend
export const getSmartSuggestions = async (
    request: SmartSuggestRequest
): Promise<SmartSuggestResponse> => {
    const prompt = buildSmartSuggestPrompt(request);

    try {
        // Route through backend's chat endpoint
        // Backend has the API key and handles Gemini calls securely
        let result: any = await authenticatedFetchJSON('/api/agents/pfmea/smartsuggest', {
            method: 'POST',
            body: JSON.stringify({
                query: prompt,
                context: {
                    mode: 'pfmea_smartsuggest',
                    processStep: request.processStepName,
                    processNumber: request.processStepNumber,
                    processFunction: request.processFunction,
                }
            })
        });

        // Handle case where result is a string (backend agent returned raw text)
        if (typeof result === 'string') {
            try {
                result = JSON.parse(cleanJson(result));
            } catch (e) {
                console.warn('Failed to parse primary AI response, using text as notes', e);
                return {
                    suggestions: [],
                    analysisNotes: result, // Return raw text if parse fails
                    recommendedActions: []
                };
            }
        }

        // Validate and enhance suggestions
        if (result.suggestions) {
            result.suggestions = result.suggestions.map((s: any) => ({
                ...s,
                actionPriority: calculateActionPriority(s.severity, s.occurrence, s.detection),
                processElement: s.processElement || infer4MCategory(s.failureCause),
            }));
        }

        return result;
    } catch (error: any) {
        console.error('SmartSuggest error:', error);

        // If backend fails, try the generic chat endpoint as fallback
        try {
            const fallbackResult: any = await authenticatedFetchJSON('/api/agents/workflow/coach', {
                method: 'POST',
                body: JSON.stringify({ prompt: prompt })
            });

            // Parse the response if it's a string
            if (typeof fallbackResult === 'string') {
                try {
                    const cleaned = cleanJson(fallbackResult);
                    return JSON.parse(cleaned);
                } catch {
                    // Try to extract JSON if it's embedded in text
                    const jsonMatch = fallbackResult.match(/\{[\s\S]*\}/);
                    if (jsonMatch) {
                        try {
                            return JSON.parse(jsonMatch[0]);
                        } catch (e) {
                            // Valid JSON structure not found
                        }
                    }

                    return {
                        suggestions: [],
                        analysisNotes: fallbackResult,
                        recommendedActions: [],
                    };
                }
            }
            return fallbackResult;
        } catch (fallbackError: any) {
            throw new Error(`Backend AI service unavailable: ${error.message}`);
        }
    }
};

// Get 4M-specific suggestions
export const get4MSuggestions = async (
    processStepName: string,
    category: 'Man' | 'Machine' | 'Material' | 'Method'
): Promise<FailureModeSuggestion[]> => {
    const categoryGuidance: Record<string, string> = {
        'Man': 'Focus on operator errors, training gaps, fatigue, skill variations',
        'Machine': 'Focus on equipment failures, calibration drift, tool wear',
        'Material': 'Focus on incoming quality, contamination, wrong material',
        'Method': 'Focus on procedure errors, work instruction gaps, setup errors',
    };

    const request: SmartSuggestRequest = {
        processStepName,
        industry: `Focus on ${category} failures: ${categoryGuidance[category]}`,
    };

    const result = await getSmartSuggestions(request);
    return result.suggestions.filter(s => s.processElement === category);
};

// Suggest controls for a specific failure mode
export const suggestControls = async (
    failureMode: string,
    failureCause: string,
    processStepName: string
): Promise<{ preventionControls: string[]; detectionControls: string[] }> => {
    const result: any = await authenticatedFetchJSON('/api/agents/workflow/coach', {
        method: 'POST',
        body: JSON.stringify({
            prompt: `Suggest prevention and detection controls for:
Process Step: ${processStepName}
Failure Mode: ${failureMode}
Cause: ${failureCause}

Return JSON: { "preventionControls": [...], "detectionControls": [...] }`
        })
    });

    if (typeof result === 'string') {
        try {
            return JSON.parse(result);
        } catch {
            return { preventionControls: [], detectionControls: [] };
        }
    }
    return {
        preventionControls: result.preventionControls || [],
        detectionControls: result.detectionControls || [],
    };
};

// Suggest S/O/D ratings based on industry benchmarks
export const suggestRatings = async (
    failureMode: string,
    failureEffect: string,
    existingControls?: { prevention?: string[]; detection?: string[] }
): Promise<{ severity: number; occurrence: number; detection: number; rationale: string }> => {
    const result: any = await authenticatedFetchJSON('/api/agents/workflow/coach', {
        method: 'POST',
        body: JSON.stringify({
            prompt: `Suggest S/O/D ratings for:
Failure Mode: ${failureMode}
Effect: ${failureEffect}
Prevention Controls: ${existingControls?.prevention?.join(', ') || 'None'}
Detection Controls: ${existingControls?.detection?.join(', ') || 'None'}

Return JSON: { "severity": N, "occurrence": N, "detection": N, "rationale": "explanation" }`
        })
    });

    if (typeof result === 'string') {
        try {
            const parsed = JSON.parse(result);
            return {
                severity: parsed.severity || 5,
                occurrence: parsed.occurrence || 5,
                detection: parsed.detection || 5,
                rationale: parsed.rationale || '',
            };
        } catch {
            return { severity: 5, occurrence: 5, detection: 5, rationale: '' };
        }
    }
    return {
        severity: result.severity || 5,
        occurrence: result.occurrence || 5,
        detection: result.detection || 5,
        rationale: result.rationale || '',
    };
};

// Export service object for easy import
export const pfmeaSmartSuggest = {
    getSuggestions: getSmartSuggestions,
    get4MSuggestions,
    suggestControls,
    suggestRatings,
    calculateActionPriority,
    infer4MCategory,
};

export default pfmeaSmartSuggest;
